import { createPopper } from '@popperjs/core'
import { pdfCompressor } from './pdf-compressor'
import imageCompressor from 'browser-image-compression'

export default {
  data () {
    return {
      placement: 'top',
      settings: {
        invalidFields: {
          titleHtml: `<b class="col-12 d-flex text-dark h5 py-2 mb-0">
            Invalid Input
          </b>`,
          size: 'sm',
          buttonSize: 'sm',
          okTitle: 'Dismiss',
          okVariant: 'dark',
          headerClass: 'py-1 px-1 d-flex justify-content-center',
          footerClass: '',
          centered: true
        },
        variants: {
          success: '',
          dark: '',
          info: '',
          warning: '',
          primary: '',
          secondary: ''
        }
      }
    }
  },
  methods: {

    async compressFile (file, onProgress = progress => progress) {
      return new Promise((resolve, reject) => {
        if (file.type === 'application/pdf') {
          if (file.size <= 3024000) { // 3MB
            onProgress(100)

            return resolve(file)
          }

          pdfCompressor(file, onProgress).then(resolve).catch(reject)
        } else {
          if (file.size <= 350000) { // 0.35MB
            onProgress(100)

            return resolve(file)
          }

          const useBackgroundProcessor = {
            maxIteration: 8,
            maxSizeMB: 0.35,
            useWebWorker: typeof Worker === 'function',
            maxWidthOrHeight: 3900,
            onProgress
          }

          imageCompressor(file, useBackgroundProcessor).then(resolve).catch(reject)
        }
      })
    },

    /**
     * Display a SweetAlert confirmation dialog with customizable options
     * @param {Object} options - Configuration options for the SweetAlert dialog
     * @param {string} [options.title='Confirmation'] - Title of the dialog
     * @param {string|Function} [options.html='Save this Record?'] - HTML content of the dialog
     * @param {string} [options.confirmButtonText='Save'] - Text for the confirm button
     * @param {boolean} [options.showCancelButton=true] - Whether to show the cancel button
     * @param {string} [options.cancelButtonText='Cancel'] - Text for the cancel button
     * @param {boolean} [options.showDenyButton=false] - Whether to show the deny button
     * @param {string} [options.denyButtonText='No'] - Text for the deny button
     * @param {boolean} [options.showLoaderOnConfirm=true] - Whether to show a loader on confirm
     * @param {boolean} [options.allowEscapeKey=false] - Whether to allow escape key to close
     * @param {boolean} [options.heightAuto=false] - Whether to auto-adjust height
     * @param {boolean} [options.buttonsStyling=false] - Whether to use default button styling
     * @param {boolean} [options.allowOutsideClick=false] - Whether to allow clicking outside to close
     * @param {string} [options.width='25rem'] - Width of the dialog
     * @param {Object} [options.customClass] - Custom CSS classes for dialog elements
     * @returns {Promise<Object>} The SweetAlert result
     */
    swalConfirm (options = {}) {
      const defaultConfig = {
        title: 'Confirmation',
        html: 'Save this Record?',
        confirmButtonText: 'Save',
        showCancelButton: true,
        cancelButtonText: 'Cancel',
        showDenyButton: false,
        denyButtonText: 'No',
        showLoaderOnConfirm: true,
        allowEscapeKey: false,
        heightAuto: false,
        buttonsStyling: false,
        allowOutsideClick: false,
        target: document.getElementById('swal-output'),
        width: '25rem'
      }

      const finalConfig = Object.assign(defaultConfig, {
        ...options,
        html: typeof options?.html === 'function' ? options?.html() : options?.html,
        customClass: {
          title: 'h5 p-2 text-warning border-bottom',
          htmlContainer: 'px-2 py-3 m-0',
          actions: 'col-12 d-flex justify-content-end p-2 border-top mx-0',
          cancelButton: 'btn btn-sm btn-outline-dark ml-1',
          denyButton: 'btn btn-sm btn-secondary ml-1',
          confirmButton: 'btn btn-sm btn-primary',
          ...options?.customClass
        }
      })

      return this.$swal.fire(finalConfig)
    },

    /**
     * Display a SweetAlert confirmation dialog with remarks/textarea input field
     * @param {Object} options - Configuration options for the SweetAlert dialog
     * @param {string} [options.title='Confirmation'] - Title of the dialog
     * @param {string|Function} [options.html] - HTML content of the dialog
     * @param {string} [options.input='textarea'] - Input type, default is textarea
     * @param {string} [options.inputLabel='Remarks'] - Label for the input field
     * @param {boolean} [options.inputAutoFocus=true] - Whether to autofocus the input field
     * @param {string} [options.width='25rem'] - Width of the dialog
     * @param {Object} [options.inputAttributes] - Attributes for the input field
     * @param {number} [options.inputAttributes.rows=10] - Number of rows for the textarea
     * @param {string} [options.inputAttributes.name='remarks'] - Name attribute for the textarea
     * @param {string} [options.inputAttributes.autocapitalize='off'] - Autocapitalization setting
     * @param {number} [options.remarksMaxLength=2500] - Maximum length of remarks
     * @param {boolean} [options.remarksRequired] - Whether remarks are required
     * @param {string} [options.confirmButtonText='Save'] - Text for the confirm button
     * @param {boolean} [options.showCancelButton=true] - Whether to show the cancel button
     * @param {string} [options.cancelButtonText='Discard'] - Text for the cancel button
     * @param {boolean} [options.showLoaderOnConfirm=true] - Whether to show a loader on confirm
     * @param {boolean} [options.allowEscapeKey=false] - Whether to allow escape key to close
     * @param {boolean} [options.buttonsStyling=false] - Whether to use default button styling
     * @param {boolean} [options.heightAuto=false] - Whether to auto-adjust height
     * @param {boolean} [options.allowOutsideClick=false] - Whether to allow clicking outside to close
     * @param {Function} [options.preConfirm] - Function to execute before confirming
     * @param {Object} [options.customClass] - Custom CSS classes for dialog elements
     * @returns {Promise<Object>} The SweetAlert result with remarks value
     */
    swalConfirmWithRemarks (options = {}) {
      const defaultConfig = {
        title: 'Confirmation',
        html: 'Save this Record?',
        input: 'textarea',
        inputLabel: 'Remarks',
        inputAutoFocus: true,
        target: document.getElementById('swal-output'),
        width: '25rem',
        inputAttributes: {
          rows: 10,
          name: 'remarks',
          autocapitalize: 'off',
          maxlength: options?.remarksMaxLength || 2500,
          placeholder: options?.remarksRequired ? 'enter remarks' : 'enter remarks ( optional )'
        },
        confirmButtonText: 'Save',
        showCancelButton: true,
        cancelButtonText: 'Discard',
        showLoaderOnConfirm: true,
        allowEscapeKey: false,
        buttonsStyling: false,
        heightAuto: false,
        allowOutsideClick: false,
        preConfirm: remarks => {
          if (typeof options?.preConfirm === 'function') {
            this.$swal.disableInput()
            return options?.preConfirm(remarks)?.finally(() => {
              this.$swal.enableInput()
            })
          }
        },
        inputValidator: remarks => {
          return new Promise(resolve => {
            if (options?.remarksMaxLength) {
              if (remarks?.length > Number(options?.remarksMaxLength)) {
                return resolve(
                  `The remarks field must be atleast lesser than or equal to ${options?.remarksMaxLength} characters`
                )
              }
            }

            if (options?.remarksRequired) {
              if (remarks?.length <= 0) {
                return resolve(
                  'The remarks field is required'
                )
              }
            }

            return resolve()
          })
        },
        didOpen: () => {
          const textArea = document.getElementById('swal2-input')
          if (textArea) {
            textArea.onkeydown = element => {
              const remarks = element.target.value

              if (options?.remarksMaxLength) {
                if (remarks?.search(/\n/g)) {
                  element.target.value = remarks.replace(/\n/g, '')
                }
              }
            }

            textArea.onchange = element => {
              const remarks = element.target.value
              if (remarks?.length > 0) {
                this.$swal.resetValidationMessage()
              }
            }
          }
        }
      }

      const finalConfig = Object.assign(defaultConfig, {
        ...options,
        html: typeof options?.html === 'function' ? options?.html() : options?.html,
        customClass: {
          title: 'h5 p-2 text-warning border-bottom',
          htmlContainer: 'px-2 py-3 m-0',
          input: 'swal-input my-1 mx-3',
          inputLabel: 'd-flex align-self-start justify-content-start font-weight-bold col-12 px-3',
          validationMessage: 'align-self-start justify-content-start bg-white px-2 my-0 text-danger',
          actions: 'col-12 d-flex justify-content-end p-2 border-top mx-0',
          cancelButton: 'btn btn-sm btn-outline-dark ml-1',
          confirmButton: 'btn btn-sm btn-primary',
          ...options?.customClass
        }
      })

      return this.$swal.fire(finalConfig)
    },

    /**
     * Display a SweetAlert success dialog
     * @param {string|Function} [html='Record has been saved'] - HTML content of the dialog
     * @param {string} [title='Successful'] - Title of the dialog
     * @returns {Promise<Object>} The SweetAlert result
     */
    swalSuccess (html = 'Record has been saved', title = 'Successful') {
      return this.$swal.fire({
        title: title,
        html: typeof html === 'function' ? html() : html,
        confirmButtonText: 'Dismiss',
        allowEscapeKey: false,
        allowOutsideClick: false,
        buttonsStyling: false,
        heightAuto: false,
        customClass: {
          title: 'h5 p-2 text-success border-bottom',
          htmlContainer: 'px-2 py-3 m-0',
          actions: 'col-12 d-flex justify-content-end p-2 border-top mx-0',
          confirmButton: 'btn btn-sm btn-primary'
        },
        target: document.getElementById('swal-output'),
        width: '25rem'
      })
    },

    /**
     * Display a SweetAlert error/validation dialog
     * @param {string|Function} [html='<h6>Your inputs seem to be incorrect or incomplete, please check and try again.</h6>'] - HTML content of the dialog
     * @param {string} [title='Validation Error'] - Title of the dialog
     * @returns {Promise<Object>} The SweetAlert result
     */
    swalInvalid (html = '<h6>Your inputs seem to be incorrect or incomplete, please check and try again.</h6>', title = 'Validation Error') {
      return this.$swal.fire({
        title: title,
        html: typeof html === 'function' ? html() : html,
        confirmButtonText: 'Dismiss',
        allowEscapeKey: false,
        allowOutsideClick: false,
        buttonsStyling: false,
        heightAuto: false,
        customClass: {
          title: 'h5 p-2 text-danger border-bottom',
          htmlContainer: 'px-2 py-3 m-0',
          actions: 'col-12 d-flex justify-content-end p-2 border-top mx-0',
          confirmButton: 'btn btn-sm btn-danger'
        },
        target: document.getElementById('swal-output'),
        width: '25rem'
      })
    },

    isMobile () {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    },

    scaleOnMobile (a, b) {
      if (this.isMobile()) {
        return Number(a.mobile) - Number(b.mobile)
      }
      return Number(b.index) - Number(a.index)
    },

    tableRowClass (row, selector) {
      if (row && selector === 'row') {
        return row.last_seen ? 'table-seen-request' : 'table-unseen-request'
      }
      return ''
    },

    iconState (icon, isActive) {
      return `${icon}-${isActive ? 'fill' : 'line'}`
    },

    colorBy (status = 'Pending') {
      switch (status) {
        case 'Pending': {
          return 'secondary'
        }
        case 'Processing': {
          return 'secondary'
        }
        case 'Disapproved': {
          return 'danger'
        }
        case 'Canceled': {
          return 'danger'
        }
        default: {
          return 'success'
        }
      }
    },

    generateId (length) {
      const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'

      let result = ''

      const charactersLength = characters.length

      for (let i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() * charactersLength))
      }

      return result
    },

    calculateDropPosition (dropDownList, component, { width }) {
      dropDownList.style.width = width
      const popper = createPopper(component.$refs.toggle, dropDownList, {
        // placement: this.placement,
        modifiers: [
          {
            name: 'offset',
            options: {
              offset: [0, -1]
            }
          },
          {
            name: 'toggleClass',
            enabled: true,
            phase: 'write',
            fn ({ state }) {
              component.$el.classList.toggle(
                'drop-up',
                state.placement === 'top'
              )
            }
          }
        ]
      })
      return () => popper.destroy()
    }
  }
}
